import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  Cog6ToothIcon,
  XMarkIcon,
  PlusIcon,
  PencilSquareIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  AdjustmentsHorizontalIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import { useUser } from '../contexts/UserContext';
import { authService } from '../services/authService';
import { azureSecurityService } from '../services/azureSecurityService';
import { apiService } from '../services/apiService';
import { AZURE_CLIENT_ID, AZURE_TENANT_ID } from '../authConfig';
import RecommendationsTab from './SecureScoreRecommendationsTab';
import { SafeHtmlContent } from '../components/SafeHtmlContent';

// ─── Helpers ────────────────────────────────────────────────────────────────

const SEV_ORDER = { High: 0, Medium: 1, Low: 2 };

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const SEV_COLORS = {
  High:   { badge: 'bg-red-100 text-red-800',    dot: '#EF4444' },
  Medium: { badge: 'bg-amber-100 text-amber-800', dot: '#F59E0B' },
  Low:    { badge: 'bg-blue-100 text-blue-800',   dot: '#3B82F6' },
};

function scoreColor(pct) {
  if (pct >= 70) return '#10B981';
  if (pct >= 40) return '#F59E0B';
  return '#EF4444';
}


function azurePortalUrl(rec) {
  const assessmentKey = rec.name;
  const parts = (rec.id ?? '').split('/');
  const subIdx = parts.indexOf('subscriptions');
  const subId = subIdx >= 0 ? parts[subIdx + 1] : null;
  if (!assessmentKey) return null;
  const base = 'https://portal.azure.com/#blade/Microsoft_Azure_Security/RecommendationsBlade';
  return subId
    ? `${base}/assessmentKey/${assessmentKey}/subscriptionId/${subId}`
    : `${base}/assessmentKey/${assessmentKey}`;
}

function stripHtml(str) {
  return str ? str.replace(/<[^>]+>/g, '') : '';
}

function formatCacheTime(date) {
  const ms = Date.now() - date.getTime();
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}d ago`;
  if (hours >= 1) return `${hours}h ago`;
  if (mins >= 1) return `${mins}m ago`;
  return 'just now';
}

function exportToExcel(visibleSubscriptions, subData, viewName) {
  const summaryRows = visibleSubscriptions
    .filter(s => subData[s.subscriptionId] && !subData[s.subscriptionId].loading)
    .map(sub => {
      const d = subData[sub.subscriptionId];
      const sc = d?.score?.score;
      const scorePct = sc?.max > 0 ? Math.round((sc.current / sc.max) * 100) : null;
      const recs = d?.assessments ?? [];
      return {
        Subscription: sub.displayName ?? sub.subscriptionId,
        'Subscription ID': sub.subscriptionId,
        'Secure Score (%)': scorePct ?? 'N/A',
        High:   recs.filter(a => a.properties?.metadata?.severity === 'High').length,
        Medium: recs.filter(a => a.properties?.metadata?.severity === 'Medium').length,
        Low:    recs.filter(a => a.properties?.metadata?.severity === 'Low').length,
        'Total Recommendations': recs.length,
      };
    });

  const recRows = [];
  visibleSubscriptions.forEach(sub => {
    const d = subData[sub.subscriptionId];
    if (!d || d.loading || d.error) return;
    (d.assessments ?? []).forEach(rec => {
      const p = rec.properties ?? {};
      const m = p.metadata ?? {};
      const resources = p.resources ?? [];
      recRows.push({
        Subscription: sub.displayName ?? sub.subscriptionId,
        'Subscription ID': sub.subscriptionId,
        Recommendation: p.displayName ?? rec.name ?? '',
        Severity: m.severity ?? 'Low',
        'Affected Resources': resources.length > 0 ? resources.map(r => r.name).join(', ') : 'Subscription level',
        'Resource Count': resources.length || 1,
        Category: (m.categories ?? []).join(', ') || '',
        'Resource Type': m.resourceType ?? '',
        Description: stripHtml(m.description),
        Remediation: stripHtml(m.remediationDescription),
        'Azure Portal Link': azurePortalUrl(rec) ?? '',
      });
    });
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows.length ? summaryRows : [{}]), 'Summary');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(recRows.length ? recRows : [{}]), 'Recommendations');

  const date = new Date().toISOString().slice(0, 10);
  const label = viewName ? `_${viewName.replace(/[^a-z0-9]/gi, '_')}` : '';
  XLSX.writeFile(wb, `SecureScore${label}_${date}.xlsx`);
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const ScoreRing = ({ pct, size = 110 }) => {
  const r = 40;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, pct)) / 100) * circ;
  const color = scoreColor(pct);
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="#E5E7EB" strokeWidth="12" />
      <circle
        cx="50" cy="50" r={r}
        fill="none"
        stroke={color}
        strokeWidth="12"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 50 50)"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text x="50" y="46" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#111827">{pct}%</text>
      <text x="50" y="63" textAnchor="middle" fontSize="9" fill="#6B7280">Secure</text>
    </svg>
  );
};

const SeverityBadge = ({ severity }) => {
  const style = SEV_COLORS[severity] ?? SEV_COLORS.Low;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.badge}`}>
      {severity ?? '—'}
    </span>
  );
};

const SummaryTile = ({ label, value, sub, color }) => (
  <div className="bg-white shadow rounded-lg p-5">
    <p className="text-sm text-gray-500">{label}</p>
    <p className={`text-3xl font-bold mt-1 ${color ?? 'text-gray-900'}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const RecommendationRow = ({ rec, expanded, onToggle }) => {
  const p = rec.properties ?? {};
  const m = p.metadata ?? {};
  const sev = m.severity ?? 'Low';
  const name = p.displayName ?? rec.name ?? 'Unknown';
  const description = m.description ?? '—';
  const remediation = m.remediationDescription ?? '—';
  const categories = (m.categories ?? []).join(', ') || '—';
  const resourceType = m.resourceType ?? '—';
  const resources = p.resources ?? [];

  return (
    <div className="border border-gray-100 rounded-md overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-gray-400 flex-shrink-0">
          {expanded
            ? <ChevronDownIcon className="h-4 w-4" />
            : <ChevronRightIcon className="h-4 w-4" />}
        </span>
        <SeverityBadge severity={sev} />
        <span className="flex-1 text-sm font-medium text-gray-800 truncate">{name}</span>
        {resources.length === 1 && (
          <span
            className="flex-shrink-0 max-w-[200px] truncate text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded px-2 py-0.5 hidden sm:block"
            title={resources[0].id ?? resources[0].name}
          >
            {resources[0].name}
          </span>
        )}
        {resources.length > 1 && (
          <span className="flex-shrink-0 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded px-2 py-0.5 hidden sm:block">
            {resources.length} resources
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 bg-gray-50 border-t border-gray-100 space-y-3 text-sm">

          {/* Affected resources */}
          {resources.length > 0 && (
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Affected {resources.length === 1 ? 'Resource' : `Resources (${resources.length})`}
              </span>
              <div className={`mt-1.5 flex flex-wrap gap-1.5 ${resources.length > 10 ? 'max-h-32 overflow-y-auto' : ''}`}>
                {resources.map((r, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center text-xs font-medium text-gray-800 bg-white border border-gray-200 rounded px-2 py-0.5"
                    title={r.id ?? r.name}
                  >
                    {r.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <span className="font-semibold text-gray-700">Description</span>
            <SafeHtmlContent
              as="p"
              className="text-gray-600 mt-0.5 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_b]:font-semibold"
              html={description}
            />
          </div>
          <div>
            <span className="font-semibold text-gray-700">Remediation</span>
            <SafeHtmlContent
              as="p"
              className="text-gray-600 mt-0.5 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_b]:font-semibold"
              html={remediation}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-1">
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</span>
              <div className="mt-1"><SeverityBadge severity={sev} /></div>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</span>
              <p className="text-gray-700 mt-1 text-xs">{categories}</p>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Resource Type</span>
              <p className="text-gray-700 mt-1 text-xs">{resourceType}</p>
            </div>
          </div>
          {azurePortalUrl(rec) && (
            <div className="pt-2 border-t border-gray-200">
              <a
                href={azurePortalUrl(rec)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                View in Azure Portal
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Azure Connection Panel ───────────────────────────────────────────────────

const ConnectPanel = ({ onConnect, connecting, error }) => (
  <div className="flex flex-col items-center justify-center py-24 gap-6">
    <div className="p-5 rounded-full bg-blue-50">
      <ShieldCheckIcon className="h-12 w-12 text-blue-600" />
    </div>
    <div className="text-center">
      <h2 className="text-xl font-semibold text-gray-900">Connect to Azure</h2>
      <p className="text-sm text-gray-500 mt-1 max-w-sm">
        Sign in with your Azure account to load Defender for Cloud secure scores.
        Your existing helpdesk login is used — no second account needed.
      </p>
    </div>
    {error && (
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm max-w-sm w-full">
        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
        {error}
      </div>
    )}
    <button
      onClick={onConnect}
      disabled={connecting}
      className="px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
    >
      {connecting ? 'Connecting…' : 'Connect to Azure'}
    </button>
  </div>
);

// ─── Settings Panel ───────────────────────────────────────────────────────────

const SettingsPanel = ({ onClose }) => {
  const clientId = AZURE_CLIENT_ID;
  const tenantId = AZURE_TENANT_ID;
  const redirectUri = window.location.origin;

  const mask = (v) => v.length > 8 ? `${v.slice(0, 4)}…${v.slice(-4)}` : v;

  return (
    <div className="bg-white shadow rounded-lg p-5 mb-6 border border-blue-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800">Azure Configuration</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Entra client and tenant IDs come from <code className="bg-gray-100 px-1 rounded">authConfig.js</code>
        {' '}(same registration as API <code className="bg-gray-100 px-1 rounded">AzureAd</code> appsettings).
        Redirect URI is the current page origin.
      </p>
      <div className="space-y-2 text-sm">
        {[
          { label: 'Client ID', value: mask(clientId) },
          { label: 'Tenant ID', value: mask(tenantId) },
          { label: 'Redirect URI', value: redirectUri },
        ].map(({ label, value }) => (
          <div key={label} className="flex items-center gap-3">
            <span className="w-28 text-gray-500 flex-shrink-0">{label}</span>
            <code className="bg-gray-100 px-2 py-0.5 rounded text-xs flex-1">{value}</code>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-4 border-t pt-3">
        Ensure the app registration has <strong>Azure Service Management &gt; user_impersonation</strong> (delegated, admin consented).
        See the comment block in <code className="bg-gray-100 px-1 rounded">azureSecurityService.js</code> for full setup instructions.
      </p>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SecureScore() {
  const { isAdmin } = useUser();

  const [azureConnected, setAzureConnected]   = useState(false);
  const [connecting, setConnecting]           = useState(false);
  const [armError, setArmError]               = useState(null);
  const [showSettings, setShowSettings]       = useState(false);

  const [subscriptions, setSubscriptions]     = useState([]);
  const [subData, setSubData]                 = useState({});  // { [subId]: { score, assessments, error, loading } }
  const [metaCatalogue, setMetaCatalogue]     = useState({});
  const [progress, setProgress]               = useState({ done: 0, total: 0 });
  const [dataLoading, setDataLoading]         = useState(false);

  const [cardExpanded, setCardExpanded]       = useState({});  // { [subId]: bool }
  const [recExpanded, setRecExpanded]         = useState({});  // { [recId]: bool }
  const [filters, setFilters]                 = useState({});  // { [subId]: 'All'|'High'|'Medium'|'Low' }
  const [sorts, setSorts]                     = useState({});  // { [subId]: 'impact'|'name' }

  const [allSubscriptions, setAllSubscriptions]             = useState([]);
  const [excludedSubIds, setExcludedSubIds]                 = useState(() => {
    try { return JSON.parse(localStorage.getItem('secureScore_excludedSubs') ?? '[]'); }
    catch { return []; }
  });
  const [showExclusionsModal, setShowExclusionsModal]       = useState(false);
  const [exclusionsModalDraft, setExclusionsModalDraft]     = useState([]);

  const [views, setViews]                                   = useState([]);
  const [activeViewId, setActiveViewId]                     = useState(null);  // null = "All"
  const [showViewModal, setShowViewModal]                   = useState(false);
  const [editingView, setEditingView]                       = useState(null);
  const [viewModalName, setViewModalName]                   = useState('');
  const [viewModalSubIds, setViewModalSubIds]               = useState([]);
  const [viewModalExcludedSubIds, setViewModalExcludedSubIds] = useState([]);
  const [viewSaving, setViewSaving]                         = useState(false);

  const [cacheInfo, setCacheInfo] = useState({ cachedAt: null, isFromCache: false });
  const [weeklyDeltas, setWeeklyDeltas] = useState({});  // { [subId]: AzureScoreWeeklyDeltaDto }

  const [searchParams, setSearchParams] = useSearchParams();
  const [contentTab, setContentTab] = useState(
    searchParams.get('tab') === 'recs' ? 'recs' : 'score'
  );

  const switchContentTab = (tab) => {
    setContentTab(tab);
    setSearchParams(tab === 'recs' ? { tab: 'recs' } : {});
  };

  // Try silent ARM auth on mount
  useEffect(() => {
    azureSecurityService.checkSilentAuth()
      .then(() => { setAzureConnected(true); })
      .catch(() => { /* needs interactive */ });
  }, []);

  // Auto-load once connected
  useEffect(() => {
    if (azureConnected) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [azureConnected]);

  // Load weekly score deltas from DB on mount — independent of Azure connection
  useEffect(() => {
    apiService.getAzureScoreDeltas()
      .then(deltas => {
        const map = {};
        for (const d of deltas ?? []) map[d.subscriptionId] = d;
        setWeeklyDeltas(map);
      })
      .catch(() => { /* non-critical */ });
  }, []);

  // Load views from DB on mount — independent of Azure connection
  useEffect(() => {
    apiService.getSecureScoreViews()
      .then(setViews)
      .catch(err => console.error('Failed to load views:', err));
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    setArmError(null);
    try {
      await azureSecurityService.connectInteractive();
      setAzureConnected(true);
    } catch (err) {
      setArmError(err.message || 'Failed to connect to Azure. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = () => {
    setAzureConnected(false);
    setSubscriptions([]);
    setSubData({});
    setProgress({ done: 0, total: 0 });
  };

  const openCreateModal = () => {
    setEditingView(null);
    setViewModalName('');
    setViewModalSubIds([]);
    setViewModalExcludedSubIds([]);
    setShowViewModal(true);
  };

  const openEditModal = (view) => {
    setEditingView(view);
    setViewModalName(view.name);
    setViewModalSubIds(view.subscriptionIds ?? []);
    setViewModalExcludedSubIds(view.excludedSubscriptionIds ?? []);
    setShowViewModal(true);
  };

  const handleSubState = (subId, newState) => {
    setViewModalSubIds(prev =>
      newState === 'include' ? [...new Set([...prev, subId])] : prev.filter(id => id !== subId)
    );
    setViewModalExcludedSubIds(prev =>
      newState === 'exclude' ? [...new Set([...prev, subId])] : prev.filter(id => id !== subId)
    );
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setEditingView(null);
  };

  const handleSaveView = async () => {
    if (!viewModalName.trim()) return;
    setViewSaving(true);
    try {
      const payload = {
        name: viewModalName.trim(),
        subscriptionIds: viewModalSubIds,
        excludedSubscriptionIds: viewModalExcludedSubIds,
      };
      if (editingView) {
        const updated = await apiService.updateSecureScoreView(editingView.id, payload);
        setViews(prev => prev.map(v => v.id === editingView.id ? updated : v));
      } else {
        const created = await apiService.createSecureScoreView(payload);
        setViews(prev => [...prev, created]);
        setActiveViewId(created.id);
      }
      closeViewModal();
    } catch (err) {
      console.error('Failed to save view:', err);
    } finally {
      setViewSaving(false);
    }
  };

  const handleDeleteView = async () => {
    if (!editingView) return;
    try {
      await apiService.deleteSecureScoreView(editingView.id);
      setViews(prev => prev.filter(v => v.id !== editingView.id));
      if (activeViewId === editingView.id) setActiveViewId(null);
      closeViewModal();
    } catch (err) {
      console.error('Failed to delete view:', err);
    }
  };

  const dragItem = useRef(null);
  const dragOver = useRef(null);

  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOver.current === null || dragItem.current === dragOver.current) {
      dragItem.current = null;
      dragOver.current = null;
      return;
    }
    const reordered = [...views];
    const [moved] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOver.current, 0, moved);
    const withOrder = reordered.map((v, i) => ({ ...v, sortOrder: i }));
    dragItem.current = null;
    dragOver.current = null;
    setViews(withOrder);
    try {
      await apiService.reorderSecureScoreViews(withOrder.map(v => ({ id: v.id, sortOrder: v.sortOrder })));
    } catch (err) {
      console.error('Failed to save view order:', err);
    }
  };

  const openExclusionsModal = () => {
    setExclusionsModalDraft([...excludedSubIds]);
    setShowExclusionsModal(true);
  };

  const saveExclusions = () => {
    const prev = [...excludedSubIds].sort().join(',');
    const next = [...exclusionsModalDraft].sort().join(',');
    const changed = prev !== next;
    setExcludedSubIds(exclusionsModalDraft);
    localStorage.setItem('secureScore_excludedSubs', JSON.stringify(exclusionsModalDraft));
    setShowExclusionsModal(false);
    if (changed && azureConnected) loadData(exclusionsModalDraft, true);
  };

  const toggleExclusion = (subId) => {
    setExclusionsModalDraft(prev =>
      prev.includes(subId) ? prev.filter(id => id !== subId) : [...prev, subId]
    );
  };

  const loadData = useCallback(async (overrideExcluded, forceRefresh = false) => {
    const effectiveExcluded = overrideExcluded ?? excludedSubIds;

    // Use backend cache if available and fresh (skip when force-refreshing)
    if (!forceRefresh) {
      try {
        const cached = await apiService.getSecureScoreCache();
        if (cached && Date.now() - new Date(cached.cachedAt).getTime() < CACHE_TTL_MS) {
          const { allSubscriptions: cachedAllSubs = [], metaCatalogue: cachedCatalogue = {}, subData: cachedSubData = {} } = cached.data;
          setAllSubscriptions(cachedAllSubs);
          const subs = cachedAllSubs.filter(s => !effectiveExcluded.includes(s.subscriptionId));
          setSubscriptions(subs);
          setMetaCatalogue(cachedCatalogue);
          setSubData(cachedSubData);
          setProgress({ done: subs.length, total: subs.length });
          setCacheInfo({ cachedAt: new Date(cached.cachedAt), isFromCache: true });
          if (subs.length <= 4) {
            setCardExpanded(Object.fromEntries(subs.map(s => [s.subscriptionId, true])));
          }
          return;
        }
      } catch (err) {
        console.warn('Cache check failed, fetching from Azure:', err);
      }
    }

    setDataLoading(true);
    setSubData({});
    setProgress({ done: 0, total: 0 });
    try {
      // Fetch subscriptions and the tenant-wide metadata catalogue in parallel.
      // The catalogue is fetched once here — not inside the per-subscription loop.
      const [allSubs, catalogue] = await Promise.all([
        azureSecurityService.getSubscriptions(),
        azureSecurityService.getAssessmentMetadataCatalogue(),
      ]);
      setAllSubscriptions(allSubs);
      const subs = allSubs.filter(s => !effectiveExcluded.includes(s.subscriptionId));
      setSubscriptions(subs);
      setMetaCatalogue(catalogue);

      if (subs.length === 0) { setDataLoading(false); return; }

      // Default expand all cards if ≤4 subscriptions
      if (subs.length <= 4) {
        setCardExpanded(Object.fromEntries(subs.map(s => [s.subscriptionId, true])));
      }

      setProgress({ done: 0, total: subs.length });

      // Collect locally so the full dataset can be saved to the backend cache in one shot
      const localSubData = {};
      await Promise.all(subs.map(async (sub) => {
        const id = sub.subscriptionId;
        setSubData(prev => ({ ...prev, [id]: { loading: true } }));
        try {
          const [score, assessments, attackPaths] = await Promise.all([
            azureSecurityService.getSecureScore(id),
            azureSecurityService.getUnhealthyAssessments(id, catalogue),
            azureSecurityService.getAttackPaths(id),
          ]);
          const data = { loading: false, score, assessments, attackPaths };
          localSubData[id] = data;
          setSubData(prev => ({ ...prev, [id]: data }));
        } catch (err) {
          const data = { loading: false, error: err.message, assessments: [] };
          localSubData[id] = data;
          setSubData(prev => ({ ...prev, [id]: data }));
        } finally {
          setProgress(prev => ({ ...prev, done: prev.done + 1 }));
        }
      }));

      // Persist to backend cache (fire-and-forget); also refresh weekly deltas afterwards
      const now = new Date();
      apiService.saveSecureScoreCache({ allSubscriptions: allSubs, metaCatalogue: catalogue, subData: localSubData })
        .then(() => {
          setCacheInfo({ cachedAt: now, isFromCache: false });
          return apiService.getAzureScoreDeltas();
        })
        .then(deltas => {
          const map = {};
          for (const d of deltas ?? []) map[d.subscriptionId] = d;
          setWeeklyDeltas(map);
        })
        .catch(err => console.warn('Failed to save security cache:', err));

    } catch (err) {
      setArmError(err.message || 'Failed to load subscription data.');
    } finally {
      setDataLoading(false);
    }
  }, [excludedSubIds]);

  const refreshSub = useCallback(async (sub) => {
    const id = sub.subscriptionId;
    setSubData(prev => ({ ...prev, [id]: { ...prev[id], loading: true, error: null } }));
    try {
      const [score, assessments, attackPaths] = await Promise.all([
        azureSecurityService.getSecureScore(id),
        azureSecurityService.getUnhealthyAssessments(id, metaCatalogue),
        azureSecurityService.getAttackPaths(id),
      ]);
      setSubData(prev => ({ ...prev, [id]: { loading: false, score, assessments, attackPaths } }));
    } catch (err) {
      setSubData(prev => ({ ...prev, [id]: { loading: false, error: err.message, assessments: prev[id]?.assessments ?? [], attackPaths: prev[id]?.attackPaths ?? [] } }));
    }
  }, [metaCatalogue]);

  if (!isAdmin()) return null;

  // ── View filtering ───────────────────────────────────────────────────────
  const activeView = activeViewId != null ? views.find(v => v.id === activeViewId) : null;

  function applyView(view, subs) {
    const included = view.subscriptionIds ?? [];
    const excluded = view.excludedSubscriptionIds ?? [];
    const base = included.length > 0 ? subs.filter(s => included.includes(s.subscriptionId)) : subs;
    return excluded.length > 0 ? base.filter(s => !excluded.includes(s.subscriptionId)) : base;
  }

  const visibleSubscriptions = activeView ? applyView(activeView, subscriptions) : subscriptions;

  // ── Derived summary numbers ──────────────────────────────────────────────
  const loadedSubs = visibleSubscriptions.filter(s => {
    const d = subData[s.subscriptionId];
    return d && !d.loading && !d.error;
  });
  const scores = loadedSubs
    .map(s => subData[s.subscriptionId]?.score)
    .filter(Boolean)
    .map(sc => sc.score?.current != null && sc.score?.max > 0
      ? Math.round((sc.score.current / sc.score.max) * 100) : null)
    .filter(v => v !== null);

  const avgScore    = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
  const totalRecs   = loadedSubs.reduce((n, s) => n + (subData[s.subscriptionId]?.assessments?.length ?? 0), 0);

  // Week-on-week overall delta for the currently visible subscriptions
  const overallDelta = (() => {
    const visible = visibleSubscriptions.map(s => weeklyDeltas[s.subscriptionId]).filter(Boolean);
    const withBoth = visible.filter(d => d.thisWeek && d.lastWeek);
    if (withBoth.length === 0) return null;
    const thisSum = withBoth.reduce((a, d) => a + d.thisWeek.currentScore, 0);
    const thisMax = withBoth.reduce((a, d) => a + d.thisWeek.maxScore, 0);
    const lastSum = withBoth.reduce((a, d) => a + d.lastWeek.currentScore, 0);
    const lastMax = withBoth.reduce((a, d) => a + d.lastWeek.maxScore, 0);
    if (thisMax <= 0 || lastMax <= 0) return null;
    return Math.round((thisSum / thisMax - lastSum / lastMax) * 100 * 10) / 10;
  })();
  const highCount   = loadedSubs.reduce((n, s) =>
    n + (subData[s.subscriptionId]?.assessments ?? [])
      .filter(a => a.properties?.metadata?.severity === 'High').length, 0);

  const azureUser   = authService.getUserName() || authService.getUserEmail() || 'Azure account';

  const isAllLoaded = progress.total > 0 && progress.done >= progress.total;
  const progressPct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ShieldCheckIcon className="h-7 w-7 text-blue-600 flex-shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Azure Secure Score</h1>
            <p className="text-sm text-gray-500">Defender for Cloud security posture</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {azureConnected && (
            <>
              <span className="text-sm text-gray-500 hidden sm:block">
                Signed in as <span className="font-medium text-gray-700">{azureUser}</span>
              </span>
              <button
                onClick={openExclusionsModal}
                className={`relative p-1.5 rounded-md hover:bg-gray-100 ${excludedSubIds.length > 0 ? 'text-amber-500 hover:text-amber-600' : 'text-gray-400 hover:text-gray-600'}`}
                title={excludedSubIds.length > 0 ? `${excludedSubIds.length} subscription${excludedSubIds.length !== 1 ? 's' : ''} excluded` : 'Manage excluded subscriptions'}
              >
                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                {excludedSubIds.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-white text-[8px] font-bold leading-none">
                    {excludedSubIds.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowSettings(s => !s)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                title="Azure config"
              >
                <Cog6ToothIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => exportToExcel(visibleSubscriptions, subData, activeView?.name ?? null)}
                disabled={dataLoading || visibleSubscriptions.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                title="Export current view to Excel"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export
              </button>
              {cacheInfo.cachedAt && (
                <span className="text-xs text-gray-400 hidden sm:block" title={cacheInfo.cachedAt.toLocaleString()}>
                  Updated {formatCacheTime(cacheInfo.cachedAt)}
                </span>
              )}
              <button
                onClick={() => loadData(undefined, true)}
                disabled={dataLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                title="Fetch fresh data from Azure"
              >
                <ArrowPathIcon className={`h-4 w-4 ${dataLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={handleDisconnect}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Settings panel ── */}
      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}

      {/* ── ARM error banner ── */}
      {armError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm mb-6">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          {armError}
        </div>
      )}

      {/* ── Not connected ── */}
      {!azureConnected && (
        <ConnectPanel onConnect={handleConnect} connecting={connecting} error={armError} />
      )}

      {/* ── Connected ── */}
      {azureConnected && (
        <>
          {/* Progress bar */}
          {dataLoading && progress.total > 0 && (
            <div className="mb-6">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Loading subscriptions…</span>
                <span>{progress.done}/{progress.total}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* ── Content type tabs ── */}
          <div className="flex gap-0.5 border-b border-gray-200 mb-6">
            {[
              { id: 'score', label: 'Secure Score',             Icon: ShieldCheckIcon       },
              { id: 'recs',  label: 'Security Recommendations',  Icon: ShieldExclamationIcon },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => switchContentTab(id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  contentTab === id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Summary tiles */}
          {isAllLoaded && contentTab === 'score' && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <SummaryTile
                label="Avg Secure Score"
                value={avgScore != null ? `${avgScore}%` : '—'}
                color={avgScore != null ? (avgScore >= 70 ? 'text-green-600' : avgScore >= 40 ? 'text-amber-600' : 'text-red-600') : 'text-gray-900'}
                sub={
                  overallDelta != null
                    ? <span className={overallDelta > 0 ? 'text-green-600' : overallDelta < 0 ? 'text-red-500' : 'text-gray-400'}>
                        {overallDelta > 0 ? '↑' : overallDelta < 0 ? '↓' : '→'} {overallDelta > 0 ? '+' : ''}{overallDelta}% vs last week
                      </span>
                    : 'across all subscriptions'
                }
              />
              <SummaryTile
                label="Subscriptions"
                value={subscriptions.length}
                sub={excludedSubIds.length > 0
                  ? `${loadedSubs.length} loaded · ${excludedSubIds.length} excluded`
                  : `${loadedSubs.length} loaded successfully`}
              />
              <SummaryTile
                label="Open Recommendations"
                value={totalRecs}
                sub="unhealthy assessments"
              />
              <SummaryTile
                label="High Severity"
                value={highCount}
                color={highCount > 0 ? 'text-red-600' : 'text-gray-900'}
                sub="requiring immediate action"
              />
            </div>
          )}

          {/* ── View tab bar ── */}
          <div className="flex items-center gap-0.5 border-b border-gray-200 mb-6 flex-wrap">
            {/* "All" tab */}
            <button
              onClick={() => setActiveViewId(null)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeViewId == null
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All {subscriptions.length > 0 && <span className="text-xs opacity-60 ml-1">({subscriptions.length})</span>}
            </button>

            {/* One tab per saved view */}
            {views.map((view, index) => {
              const isActive = activeViewId === view.id;
              const count = applyView(view, subscriptions).length;
              return (
                <div
                  key={view.id}
                  className="flex items-center -mb-px cursor-grab active:cursor-grabbing select-none"
                  draggable
                  onDragStart={() => { dragItem.current = index; }}
                  onDragEnter={() => { dragOver.current = index; }}
                  onDragOver={e => e.preventDefault()}
                  onDragEnd={handleDragEnd}
                >
                  <button
                    onClick={() => setActiveViewId(view.id)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      isActive
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {view.name}
                    {subscriptions.length > 0 && <span className="text-xs opacity-60 ml-1">({count})</span>}
                  </button>
                  {isActive && (
                    <button
                      onClick={() => openEditModal(view)}
                      className="p-1 text-gray-400 hover:text-gray-600 -ml-2 mb-0.5"
                      title="Edit view"
                    >
                      <PencilSquareIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}

            {/* New view button — shown once Azure is connected */}
            {azureConnected && (
              <button
                onClick={openCreateModal}
                className="flex items-center gap-1 px-3 py-1.5 ml-1 text-sm text-gray-500 hover:text-gray-800 rounded-md hover:bg-gray-100 mb-0.5"
              >
                <PlusIcon className="h-4 w-4" />
                New View
              </button>
            )}
          </div>

          {/* ── Secure Score tab content ── */}
          {contentTab === 'score' && (
          <>
          {visibleSubscriptions.length === 0 && !dataLoading && subscriptions.length > 0 && (
            <p className="text-center text-gray-400 py-16">No subscriptions match this view.</p>
          )}
          {subscriptions.length === 0 && !dataLoading && (
            <p className="text-center text-gray-400 py-16">No enabled subscriptions found.</p>
          )}

          <div className="space-y-4">
            {visibleSubscriptions.map(sub => {
              const id   = sub.subscriptionId;
              const name = sub.displayName ?? id;
              const d    = subData[id];
              const open = cardExpanded[id] ?? false;

              const scoreProp = d?.score;
              const scorePct  = scoreProp?.score?.max > 0
                ? Math.round((scoreProp.score.current / scoreProp.score.max) * 100)
                : null;

              const allRecs   = d?.assessments ?? [];
              const filter    = filters[id] ?? 'All';
              const sort      = sorts[id] ?? 'impact';
              const recFilter = filter === 'All' ? allRecs : allRecs.filter(
                a => a.properties?.metadata?.severity === filter
              );
              const recSorted = [...recFilter].sort((a, b) => {
                if (sort === 'impact') {
                  const sa = SEV_ORDER[a.properties?.metadata?.severity] ?? 99;
                  const sb = SEV_ORDER[b.properties?.metadata?.severity] ?? 99;
                  return sa - sb;
                }
                return (a.properties?.displayName ?? '').localeCompare(b.properties?.displayName ?? '');
              });

              return (
                <div key={id} className="bg-white shadow rounded-lg overflow-hidden">
                  {/* Card header — always visible */}
                  <div
                    onClick={() => setCardExpanded(prev => ({ ...prev, [id]: !open }))}
                    className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="text-gray-400">
                      {open
                        ? <ChevronDownIcon className="h-5 w-5" />
                        : <ChevronRightIcon className="h-5 w-5" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{id}</p>
                    </div>

                    {d?.loading && (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Loading…
                      </span>
                    )}
                    {d?.error && (
                      <span className="flex items-center gap-1 text-xs text-red-500">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Error
                      </span>
                    )}
                    {!d?.loading && !d?.error && allRecs.length > 0 && (
                      <span className="text-xs text-gray-500">{allRecs.length} open</span>
                    )}

                    <button
                      onClick={e => { e.stopPropagation(); refreshSub(sub); }}
                      disabled={d?.loading}
                      title="Refresh this subscription"
                      className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-40 flex-shrink-0 transition-colors"
                    >
                      <ArrowPathIcon className={`h-4 w-4 ${d?.loading ? 'animate-spin' : ''}`} />
                    </button>

                    {scorePct != null && (
                      <div className="flex flex-col items-center gap-1">
                        <ScoreRing pct={scorePct} size={80} />
                        {(() => {
                          const delta = weeklyDeltas[id];
                          if (!delta || delta.deltaPercentage == null || !delta.lastWeek) return null;
                          const d = delta.deltaPercentage;
                          if (d === null) return null;
                          const cls = d > 0 ? 'text-green-600' : d < 0 ? 'text-red-500' : 'text-gray-400';
                          return (
                            <span className={`text-xs font-medium ${cls}`} title="Week-on-week change">
                              {d > 0 ? '↑ +' : d < 0 ? '↓ ' : '→ '}{d}%
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  {open && (
                    <div className="border-t border-gray-100 px-5 py-4">
                      {d?.error && (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2 mb-4">
                          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                          {d.error}
                        </div>
                      )}

                      {!d?.loading && !d?.error && allRecs.length === 0 && (
                        <p className="text-sm text-green-600 font-medium py-2">
                          ✓ All recommendations healthy
                        </p>
                      )}

                      {allRecs.length > 0 && (
                        <>
                          {/* Filter + sort bar */}
                          <div className="flex flex-wrap items-center gap-3 mb-4">
                            <div className="flex items-center gap-1.5">
                              <FunnelIcon className="h-4 w-4 text-gray-400" />
                              {['All', 'High', 'Medium', 'Low'].map(f => (
                                <button
                                  key={f}
                                  onClick={() => setFilters(prev => ({ ...prev, [id]: f }))}
                                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                                    filter === f
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  {f}
                                  {f !== 'All' && (
                                    <span className="ml-1 opacity-70">
                                      ({allRecs.filter(a => a.properties?.metadata?.severity === f).length})
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                            <div className="flex items-center gap-1 ml-auto text-xs text-gray-500">
                              <span>Sort:</span>
                              {[{ v: 'impact', l: 'Impact' }, { v: 'name', l: 'Name A–Z' }].map(opt => (
                                <button
                                  key={opt.v}
                                  onClick={() => setSorts(prev => ({ ...prev, [id]: opt.v }))}
                                  className={`px-2 py-1 rounded transition-colors ${
                                    sort === opt.v
                                      ? 'bg-blue-50 text-blue-700 font-medium'
                                      : 'text-gray-500 hover:bg-gray-100'
                                  }`}
                                >
                                  {opt.l}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Recommendations list */}
                          <div className="space-y-1.5">
                            {recSorted.length === 0 && (
                              <p className="text-sm text-gray-400 py-2">No recommendations match this filter.</p>
                            )}
                            {recSorted.map(rec => {
                              const recId = rec.id ?? rec.name;
                              return (
                                <RecommendationRow
                                  key={recId}
                                  rec={rec}
                                  expanded={!!recExpanded[recId]}
                                  onToggle={() => setRecExpanded(prev => ({ ...prev, [recId]: !prev[recId] }))}
                                />
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
          )}

          {/* ── Security Recommendations tab content ── */}
          {contentTab === 'recs' && (
            <RecommendationsTab
              visibleSubscriptions={visibleSubscriptions}
              subData={subData}
              isAllLoaded={isAllLoaded}
            />
          )}
        </>
      )}

      {/* ── Create / Edit view modal ── */}
      {showViewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-900">
                {editingView ? 'Edit View' : 'New View'}
              </h3>
              <button onClick={closeViewModal} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">View name</label>
                <input
                  type="text"
                  value={viewModalName}
                  onChange={e => setViewModalName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveView()}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Clients, Internal, Production"
                  autoFocus
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Subscriptions</label>
                  <span className="text-xs text-gray-400">
                    {viewModalSubIds.length > 0
                      ? `${viewModalSubIds.length} included`
                      : 'All included'}
                    {viewModalExcludedSubIds.length > 0 && `, ${viewModalExcludedSubIds.length} excluded`}
                  </span>
                </div>
                {subscriptions.length > 0 ? (
                  <>
                    <div className="flex items-center gap-4 mb-2 text-xs text-gray-500 px-1">
                      <span className="flex-1">Leave all unset to show every subscription in this view.</span>
                    </div>
                    <div className="space-y-0.5 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-2">
                      {subscriptions.map(sub => {
                        const subState = viewModalSubIds.includes(sub.subscriptionId)
                          ? 'include'
                          : viewModalExcludedSubIds.includes(sub.subscriptionId)
                            ? 'exclude'
                            : 'none';
                        return (
                          <div
                            key={sub.subscriptionId}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded ${
                              subState === 'include' ? 'bg-blue-50' : subState === 'exclude' ? 'bg-red-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <span className="flex-1 text-sm text-gray-800 truncate">
                              {sub.displayName ?? sub.subscriptionId}
                            </span>
                            <span className="text-xs text-gray-400 font-mono flex-shrink-0 hidden sm:block truncate max-w-[140px]">
                              {sub.subscriptionId}
                            </span>
                            <div className="flex rounded overflow-hidden border border-gray-200 flex-shrink-0">
                              {[
                                { key: 'include', label: '+ Inc', active: 'bg-blue-600 text-white', inactive: 'bg-white text-gray-500 hover:bg-blue-50' },
                                { key: 'none',    label: '—',     active: 'bg-gray-200 text-gray-700', inactive: 'bg-white text-gray-400 hover:bg-gray-100' },
                                { key: 'exclude', label: '− Exc', active: 'bg-red-600 text-white', inactive: 'bg-white text-gray-500 hover:bg-red-50' },
                              ].map(opt => (
                                <button
                                  key={opt.key}
                                  type="button"
                                  onClick={() => handleSubState(sub.subscriptionId, opt.key)}
                                  className={`px-2 py-0.5 text-xs font-medium transition-colors border-r last:border-r-0 border-gray-200 ${
                                    subState === opt.key ? opt.active : opt.inactive
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-400 border border-gray-200 rounded-md p-3">
                    Connect to Azure first to select subscriptions.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <div>
                {editingView && (
                  <button
                    onClick={handleDeleteView}
                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                  >
                    Delete view
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={closeViewModal}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveView}
                  disabled={!viewModalName.trim() || viewSaving}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {viewSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Exclusions modal ── */}
      {showExclusionsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="font-semibold text-gray-900">Excluded Subscriptions</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Excluded subscriptions are skipped entirely — not loaded, not counted.
                </p>
              </div>
              <button onClick={() => setShowExclusionsModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4">
              {allSubscriptions.length === 0 ? (
                <p className="text-sm text-gray-400 border border-gray-200 rounded-md p-3">
                  Connect to Azure first to manage subscriptions.
                </p>
              ) : (
                <div className="space-y-0.5 max-h-80 overflow-y-auto border border-gray-200 rounded-md p-2">
                  {allSubscriptions.map(sub => {
                    const isExcluded = exclusionsModalDraft.includes(sub.subscriptionId);
                    return (
                      <label
                        key={sub.subscriptionId}
                        className={`flex items-center gap-3 px-2 py-2 rounded cursor-pointer transition-colors ${
                          isExcluded ? 'bg-red-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isExcluded}
                          onChange={() => toggleExclusion(sub.subscriptionId)}
                          className="h-4 w-4 accent-red-600 rounded border-gray-300 flex-shrink-0"
                        />
                        {isExcluded && <NoSymbolIcon className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />}
                        <span className={`flex-1 text-sm truncate ${isExcluded ? 'text-red-700 line-through' : 'text-gray-800'}`}>
                          {sub.displayName ?? sub.subscriptionId}
                        </span>
                        <span className="text-xs text-gray-400 font-mono flex-shrink-0 hidden sm:block truncate max-w-[140px]">
                          {sub.subscriptionId}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <span className="text-xs text-gray-500">
                {exclusionsModalDraft.length > 0
                  ? `${exclusionsModalDraft.length} subscription${exclusionsModalDraft.length !== 1 ? 's' : ''} will be excluded`
                  : 'No exclusions — all subscriptions will load'}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowExclusionsModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={saveExclusions}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save & Reload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
